import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  AlertTriangle, 
  Calculator, 
  FileText, 
  TrendingUp, 
  TrendingDown,
  CheckCircle,
  XCircle
} from 'lucide-react';

const ChangesMateriality = ({ onSubmit, onNext, onPrevious, initialData = {} }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    requiresChange: initialData.requiresChange || false,
    changeType: initialData.changeType || 'numerical',
    deltaTCO2e: initialData.deltaTCO2e || 0,
    deltaKPIs: initialData.deltaKPIs || {},
    materialityRationale: initialData.materialityRationale || '',
    isMaterial: initialData.isMaterial || false
  });

  const [kpiChanges, setKpiChanges] = useState([
    { id: 1, name: 'Scope 1 Emissions', current: 1250, new: 1250, delta: 0, unit: 'tCO₂e' },
    { id: 2, name: 'Scope 2 Emissions', current: 850, new: 850, delta: 0, unit: 'tCO₂e' },
    { id: 3, name: 'Scope 3 Emissions', current: 2100, new: 2100, delta: 0, unit: 'tCO₂e' },
    { id: 4, name: 'Total Emissions', current: 4200, new: 4200, delta: 0, unit: 'tCO₂e' },
    { id: 5, name: 'Emissions Intensity', current: 0.85, new: 0.85, delta: 0, unit: 'tCO₂e/€M' }
  ]);

  const handleRequiresChangeToggle = (value) => {
    setFormData({
      ...formData,
      requiresChange: value,
      isMaterial: value && formData.isMaterial
    });
  };

  const handleChangeTypeChange = (type) => {
    setFormData({
      ...formData,
      changeType: type
    });
  };

  const handleKPIChange = (id, field, value) => {
    const updatedKPIs = kpiChanges.map(kpi => {
      if (kpi.id === id) {
        const updated = { ...kpi, [field]: value };
        if (field === 'new') {
          updated.delta = updated.new - updated.current;
        }
        return updated;
      }
      return kpi;
    });
    setKpiChanges(updatedKPIs);
  };

  const calculateTotalDelta = () => {
    return kpiChanges.reduce((sum, kpi) => sum + kpi.delta, 0);
  };


  const getDeltaIcon = (delta) => {
    if (delta > 0) return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (delta < 0) return <TrendingDown className="w-4 h-4 text-green-500" />;
    return <CheckCircle className="w-4 h-4 text-gray-400" />;
  };

  const getDeltaColor = (delta) => {
    if (delta > 0) return 'text-red-600';
    if (delta < 0) return 'text-green-600';
    return 'text-gray-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          {t('workflow.auditQA.changesMateriality.title')}
        </h2>
        
        <p className="text-gray-600 mb-8">
          {t('workflow.auditQA.changesMateriality.description')}
        </p>

        {/* Requires Change Toggle */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('workflow.auditQA.changesMateriality.requiresChange')}
          </h3>
          
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={() => handleRequiresChangeToggle(true)}
              className={`flex items-center px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                formData.requiresChange
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              <XCircle className="w-5 h-5 mr-2" />
              {t('workflow.auditQA.changesMateriality.yes')}
            </button>
            
            <button
              type="button"
              onClick={() => handleRequiresChangeToggle(false)}
              className={`flex items-center px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                !formData.requiresChange
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              {t('workflow.auditQA.changesMateriality.no')}
            </button>
          </div>
        </div>

        {formData.requiresChange && (
          <>
            {/* Change Type */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('workflow.auditQA.changesMateriality.changeType')}
              </h3>
              
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => handleChangeTypeChange('numerical')}
                  className={`flex items-center px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                    formData.changeType === 'numerical'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  {t('workflow.auditQA.changesMateriality.numerical')}
                </button>
                
                <button
                  type="button"
                  onClick={() => handleChangeTypeChange('narrative')}
                  className={`flex items-center px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                    formData.changeType === 'narrative'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <FileText className="w-5 h-5 mr-2" />
                  {t('workflow.auditQA.changesMateriality.narrativeOnly')}
                </button>
              </div>
            </div>

            {/* Delta Summary */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('workflow.auditQA.changesMateriality.deltaSummary')}
              </h3>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <span className="text-sm font-medium text-gray-700">
                      {t('workflow.auditQA.changesMateriality.deltaTCO2e')}
                    </span>
                    <div className="flex items-center">
                      {getDeltaIcon(calculateTotalDelta())}
                      <span className={`ml-2 font-semibold ${getDeltaColor(calculateTotalDelta())}`}>
                        {calculateTotalDelta() > 0 ? '+' : ''}{calculateTotalDelta().toFixed(1)} tCO₂e
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <span className="text-sm font-medium text-gray-700">
                      {t('workflow.auditQA.changesMateriality.materialityStatus')}
                    </span>
                    <div className="flex items-center">
                      {formData.isMaterial ? (
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                      <span className={`ml-2 font-semibold ${
                        formData.isMaterial ? 'text-orange-600' : 'text-green-600'
                      }`}>
                        {formData.isMaterial 
                          ? t('workflow.auditQA.changesMateriality.material')
                          : t('workflow.auditQA.changesMateriality.immaterial')
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* KPI Changes Table */}
            {formData.changeType === 'numerical' && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('workflow.auditQA.changesMateriality.kpiChanges')}
                </h3>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('workflow.auditQA.changesMateriality.kpi')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('workflow.auditQA.changesMateriality.current')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('workflow.auditQA.changesMateriality.new')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('workflow.auditQA.changesMateriality.delta')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {kpiChanges.map((kpi) => (
                        <tr key={kpi.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {kpi.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {kpi.current} {kpi.unit}
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              value={kpi.new}
                              onChange={(e) => handleKPIChange(kpi.id, 'new', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              step="0.1"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center">
                              {getDeltaIcon(kpi.delta)}
                              <span className={`ml-2 text-sm font-medium ${getDeltaColor(kpi.delta)}`}>
                                {kpi.delta > 0 ? '+' : ''}{kpi.delta.toFixed(1)} {kpi.unit}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Materiality Rationale */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('workflow.auditQA.changesMateriality.materialityRationale')}
              </h3>
              
              <textarea
                value={formData.materialityRationale}
                onChange={(e) => setFormData({...formData, materialityRationale: e.target.value})}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('workflow.auditQA.changesMateriality.rationalePlaceholder')}
              />
            </div>

            {/* Material Change Banner */}
            {formData.isMaterial && (
              <div className="mb-8">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertTriangle className="w-6 h-6 text-orange-500 mr-3" />
                    <div>
                      <h4 className="text-lg font-semibold text-orange-800">
                        {t('workflow.auditQA.changesMateriality.materialChangeBanner')}
                      </h4>
                      <p className="text-orange-700 mt-1">
                        {t('workflow.auditQA.changesMateriality.materialChangeDescription')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <button
                      type="button"
                      className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors"
                    >
                      {t('workflow.auditQA.changesMateriality.createV2')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="button"
            className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            {t('workflow.auditQA.changesMateriality.runPreChecks')}
          </button>
        </div>
    </div>
  );
};

export default ChangesMateriality;
